const ERR = Symbol("ERR");
interface Err {
  [ERR]: true;
  error: unknown;
  type?: ErrTypes;
}

type ErrTypes =
  | "GENERIC_ERROR"
  | "API_NOT_OK_RESPONSE"
  | "UNAUTHORIZED"
  | "ACCOUNT_EXISTS";

export function isErr(x: unknown): x is Err {
  return typeof x === "object" && x != null && ERR in x;
}

export function Err(message: string, type?: ErrTypes): Err {
  return { [ERR]: true, error: message, type };
}

/**
 * Convert a function that throws into a function that returns an Err
 */
export async function tryFail<T>(
  f: (() => Promise<T>) | (() => T),
): Promise<T | Err> {
  try {
    return await f();
  } catch (e) {
    return { [ERR]: true, error: e };
  }
}

/** Throw an error if the value is an Err */
export function assertOk<T>(x: T | Err) {
  if (isErr(x)) {
    if (x.error instanceof Error) {
      throw x.error;
    } else {
      const errorMsg = `Error: ${String(x.error)}${
        x.type ? ` | Type: ${x.type}` : ""
      }`;
      throw new Error(errorMsg);
    }
  }
}

export function errReturnify<In extends unknown[], Out>(
  f: (...args: In) => Out,
): (...args: In) => Out | Err {
  return (...args: In) => {
    try {
      return f(...args);
    } catch (e) {
      if (e instanceof Error) return Err(e.message);
      return Err(`unknown error in ${f.name}: ${JSON.stringify(e)}`);
    }
  };
}

// Above is deprecated, use below instead

export class KnownGenericError extends Error {
  readonly _tag = "KnownGenericError";
  readonly userFriendlyMessage: string;
  constructor({ userFriendlyMessage }: { userFriendlyMessage: string }) {
    super(userFriendlyMessage);
    this.userFriendlyMessage = userFriendlyMessage;
  }
}

// export function extractKnownError(x: Error): KnownGenericError {
//   if (isFiberFailureOfKnownGenericError(x)) {
//     return (
//       x.toJSON() as { cause: { failure: { userFriendlyMessage: string } } }
//     )["cause"]["failure"]["userFriendlyMessage"];
//   }
//   return new KnownGenericError({
//     userFriendlyMessage: "Unknown error",
//   });
// }
